import { useEffect, useMemo, useState } from 'react';
import type { ClipboardEvent, KeyboardEvent } from 'react';
import { Box, TextField } from '@mui/material';

export type OtpInputProps = {
  length?: number;
  /** Disabled state — passed through to each cell. */
  disabled?: boolean;
  /** True to auto-focus the first cell on mount. */
  autoFocus?: boolean;
  /** Fired on every keystroke. */
  onChange?: (code: string) => void;
  /** Fired once the user has filled the last cell. The form submits from here. */
  onComplete?: (code: string) => void;
  /** Optional: drives the value externally so the parent can clear / re-init. */
  value?: string;
};

/**
 * Six-cell numeric OTP input. Cursor advances on entry, backspace
 * retreats. `onComplete` fires once all cells are filled — the parent
 * submits straight from there so users don't have to click anything.
 *
 * Paste is supported: paste a 6-digit string into any cell and it
 * splits across the row.
 */
export function OtpInput({
  length = 6,
  disabled,
  autoFocus = true,
  onChange,
  onComplete,
  value,
}: OtpInputProps) {
  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length }, (_, i) => value?.[i] ?? ''),
  );
  const refs = useMemo(() => Array.from({ length }, () => ({ current: null as HTMLInputElement | null })), [length]);

  // Sync from a controlled value. Clearing the value (parent passes '')
  // resets every cell; we don't react on partial values to avoid fighting
  // the user's keystrokes mid-typing.
  useEffect(() => {
    if (value === '' || value === undefined) {
      setDigits(Array.from({ length }, () => ''));
    } else if (value.length === length) {
      setDigits(value.split(''));
    }
  }, [value, length]);

  useEffect(() => {
    if (autoFocus) refs[0].current?.focus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setCell = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(-1); // last char typed
    setDigits((prev) => {
      const next = [...prev];
      next[index] = digit;
      const joined = next.join('');
      onChange?.(joined);
      // Advance the cursor on entry; if completed, fire onComplete.
      if (digit && index < length - 1) {
        refs[index + 1].current?.focus();
      } else if (digit && index === length - 1 && next.every((c) => c)) {
        // Defer slightly so React commits state before parent's submit fires.
        setTimeout(() => onComplete?.(joined), 0);
      }
      return next;
    });
  };

  const onKeyDown = (index: number) => (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (digits[index]) {
        // Clear current cell first.
        setDigits((prev) => {
          const next = [...prev]; next[index] = '';
          onChange?.(next.join(''));
          return next;
        });
      } else if (index > 0) {
        // Empty cell — jump back.
        refs[index - 1].current?.focus();
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      refs[index - 1].current?.focus();
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      refs[index + 1].current?.focus();
    }
  };

  const onPaste = (index: number) => (e: ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    e.preventDefault();
    setDigits((prev) => {
      const next = [...prev];
      for (let i = 0; i < pasted.length && index + i < length; i++) {
        next[index + i] = pasted[i];
      }
      const joined = next.join('');
      onChange?.(joined);
      const lastFilled = Math.min(index + pasted.length, length) - 1;
      const target = lastFilled < length - 1 ? lastFilled + 1 : lastFilled;
      refs[target].current?.focus();
      if (next.every((c) => c)) {
        setTimeout(() => onComplete?.(joined), 0);
      }
      return next;
    });
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: `repeat(${length}, 1fr)`,
        gap: 1,
        width: '100%',
      }}
    >
      {digits.map((digit, i) => (
        <TextField
          key={i}
          inputRef={(el: HTMLInputElement | null) => { refs[i].current = el; }}
          value={digit}
          onChange={(e) => setCell(i, e.target.value)}
          onKeyDown={onKeyDown(i)}
          onPaste={onPaste(i)}
          onFocus={(e) => e.currentTarget.select()}
          disabled={disabled}
          autoComplete="one-time-code"
          inputMode="numeric"
          // The visible spec calls this 1 character because we keep only the
          // last digit on input.
          inputProps={{
            maxLength: 1,
            pattern: '[0-9]*',
            'aria-label': `OTP digit ${i + 1}`,
            style: { textAlign: 'center', fontFamily: 'monospace', fontSize: 28, fontWeight: 600, padding: 8 },
          }}
        />
      ))}
    </Box>
  );
}
